import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse, IApiPaginatedResponse } from "@/types";
import type {
  IOvertimeRequest,
  OvertimeRequestStatus,
  OvertimeRequestType,
} from "@/types/overtime";

// ─── Query / mutation param shapes ───────────────────────────────────────────

export interface IGetCompanyOvertimeRequestsParams {
  page?: number;
  limit?: number;
  status?: OvertimeRequestStatus;
  type?: OvertimeRequestType;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  userId?: number;
  branchId?: number;
}

export interface ICreateCompanyOvertimeRequestData {
  userId: number;
  date: string;
  requestedStart: string;
  requestedEnd: string;
  reason?: string;
  type?: OvertimeRequestType;
  attachment?: File | null;
}

export interface IRejectCompanyOvertimeData {
  id: number | string;
  reasonOfRejection: string;
}

export interface IGetCompanyOvertimeQuotaParams {
  userId: number;
  date: string;
}

export interface IOvertimeStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  cancelled: number;
  expired: number;
}
export interface IOvertimeQuota {
  daily: { max: number | null };
  weekly: { max: number | null; used: number; remaining: number | null };
  monthly: { max: number | null; used: number; remaining: number | null };
}

// ─── API slice ────────────────────────────────────────────────────────────────

export const companyOvertimeApi = createApi({
  reducerPath: "companyOvertimeApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "getCompanyOvertimeRequests",
    "getCompanyOvertimeRequestById",
    "getCompanyOvertimeStats",
  ],
  endpoints: (builder) => ({
    getCompanyOvertimeRequests: builder.query<
      IApiPaginatedResponse<IOvertimeRequest[]>,
      IGetCompanyOvertimeRequestsParams
    >({
      query: (params) => ({
        url: "/company/overtime-requests/paginated",
        method: "GET",
        params,
      }),
      providesTags: ["getCompanyOvertimeRequests"],
    }),

    getCompanyOvertimeStats: builder.query<IApiResponse<IOvertimeStats>, void>({
      query: () => ({
        url: "/company/overtime-requests/stats",
        method: "GET",
      }),
      providesTags: ["getCompanyOvertimeStats"],
    }),

    getCompanyOvertimeRequestById: builder.query<
      IApiResponse<IOvertimeRequest>,
      { id: number | string }
    >({
      query: ({ id }) => ({
        url: `/company/overtime-requests/${id}`,
        method: "GET",
      }),
      providesTags: ["getCompanyOvertimeRequestById"],
    }),

    createCompanyOvertimeRequest: builder.mutation<
      IApiResponse<IOvertimeRequest>,
      ICreateCompanyOvertimeRequestData
    >({
      query: (body) => {
        const { attachment, ...rest } = body;
        const formData = new FormData();
        formData.append("userId", String(rest.userId));
        formData.append("date", rest.date);
        formData.append("requestedStart", rest.requestedStart);
        formData.append("requestedEnd", rest.requestedEnd);
        if (rest.reason) formData.append("reason", rest.reason);
        if (rest.type) formData.append("type", rest.type);
        if (attachment instanceof File) formData.append("attachment", attachment);
        return {
          url: "/company/overtime-requests",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["getCompanyOvertimeRequests", "getCompanyOvertimeStats"],
    }),

    approveCompanyOvertime: builder.mutation<
      IApiResponse<IOvertimeRequest>,
      { id: number | string }
    >({
      query: ({ id }) => ({
        url: `/company/overtime-requests/${id}/approve`,
        method: "POST",
      }),
      invalidatesTags: [
        "getCompanyOvertimeRequests",
        "getCompanyOvertimeRequestById",
        "getCompanyOvertimeStats",
      ],
    }),

    rejectCompanyOvertime: builder.mutation<
      IApiResponse<IOvertimeRequest>,
      IRejectCompanyOvertimeData
    >({
      query: ({ id, reasonOfRejection }) => ({
        url: `/company/overtime-requests/${id}/reject`,
        method: "POST",
        body: { reasonOfRejection },
      }),
      invalidatesTags: [
        "getCompanyOvertimeRequests",
        "getCompanyOvertimeRequestById",
        "getCompanyOvertimeStats",
      ],
    }),

    getCompanyOvertimeQuota: builder.query<
      IApiResponse<IOvertimeQuota | null>,
      IGetCompanyOvertimeQuotaParams
    >({
      query: ({ userId, date }) => ({
        url: '/company/overtime-requests/quota',
        method: 'GET',
        params: { userId, date },
      }),
    }),
  }),
});

export const {
  useGetCompanyOvertimeRequestsQuery,
  useGetCompanyOvertimeStatsQuery,
  useGetCompanyOvertimeRequestByIdQuery,
  useCreateCompanyOvertimeRequestMutation,
  useApproveCompanyOvertimeMutation,
  useRejectCompanyOvertimeMutation,
  useGetCompanyOvertimeQuotaQuery,
} = companyOvertimeApi;
